import React from 'react';
import './box.scss';
import classnames from 'classnames';

interface PaddingDirection {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

type Direction = keyof PaddingDirection;

export type Padding =
  | number
  | ({
      vertical?: number;
      horizontal?: number;
    } & PaddingDirection);

interface Props {
  children: React.ReactNode;
  padding?: Padding;
  tabletPadding?: Padding;
  desktopPadding?: Padding;
  inlineBlock?: boolean;
}

export function Box({ padding, tabletPadding, desktopPadding, children, inlineBlock }: Props): JSX.Element {
  const completePadding = getCompletePadding(padding);
  const completePaddingTablet = getCompletePadding(tabletPadding, completePadding);
  const completePaddingDesktop = getCompletePadding(desktopPadding, completePaddingTablet);

  return (
    <div
      className={classnames('box', { 'box--inline-block': inlineBlock })}
      style={{
        ...getPaddingStyle(completePadding, 'mobile'),
        ...getPaddingStyle(completePaddingTablet, 'tablet'),
        ...getPaddingStyle(completePaddingDesktop, 'desktop'),
      }}
    >
      {children}
    </div>
  );
}

const directions: Direction[] = ['top', 'bottom', 'left', 'right'];

function getPaddingStyle(padding: PaddingDirection, viewport: 'mobile' | 'tablet' | 'desktop'): React.CSSProperties {
  const style: Record<string, string> = {};

  for (const direction of directions) {
    style[`--padding-${viewport}-${direction}`] = `${padding[direction] ?? 0}px`;
  }

  return style;
}

function getCompletePadding(padding: Padding = 0, fallback?: PaddingDirection): PaddingDirection {
  if (!padding) return fallback || {};

  const completePadding: PaddingDirection = {};

  for (const direction of directions) {
    const singlePadding = getSinglePadding(padding, direction);
    completePadding[direction] = singlePadding || fallback?.[direction];
  }

  return completePadding;
}

function getSinglePadding(padding: Padding, direction: 'top' | 'bottom' | 'left' | 'right'): number | undefined {
  if (!padding) return undefined;

  if (typeof padding === 'number') return padding;

  const mergedDirection = ['top', 'bottom'].includes(direction) ? 'vertical' : 'horizontal';

  return padding[mergedDirection] || padding[direction];
}
